import type {
  ClassificationTaxonomy,
  FamilyMemberSelection,
  TaxonomyFamily,
} from "../types/taxonomy";
import type { ClassificationLabel } from "../types/review";

/**
 * Given a label value (canonical or alias), find which family + member it
 * belongs to. Returns null for labels that are not part of any taxonomy
 * family (legacy / invalid / unknown / empty).
 */
export const resolveLabelToFamily = (
  label: string | null | undefined,
  taxonomy: ClassificationTaxonomy | null | undefined
): { family: TaxonomyFamily; memberIndex: number } | null => {
  if (!label || !taxonomy) return null;
  const norm = label.trim();
  for (const family of taxonomy.families) {
    const memberIndex = family.members.findIndex(
      (m) => m.canonical === norm || m.aliases.includes(norm)
    );
    if (memberIndex !== -1) {
      return { family, memberIndex };
    }
  }
  return null;
};

/**
 * Build a map: family.key → { label, memberIndex } that reflects the current
 * state of an image based on its labels array. Only considers family labels.
 */
export const buildCurrentFamilyState = (
  labels: ClassificationLabel[],
  taxonomy: ClassificationTaxonomy | null | undefined
): Map<string, { label: ClassificationLabel; memberIndex: number }> => {
  const result = new Map<
    string,
    { label: ClassificationLabel; memberIndex: number }
  >();
  if (!taxonomy) return result;
  for (const label of labels) {
    if (!label.is_active) continue;
    const resolved = resolveLabelToFamily(label.final_label, taxonomy);
    if (resolved) {
      result.set(resolved.family.key, {
        label,
        memberIndex: resolved.memberIndex,
      });
    }
  }
  return result;
};

/**
 * Given a family + selected member index, return the canonical label string
 * that should be sent to the backend as human_label.
 */
export const memberToCanonical = (
  family: TaxonomyFamily,
  selection: FamilyMemberSelection
): string | null => {
  if (selection === "none") return null;
  return family.members[selection]?.canonical ?? null;
};

/** Split labels into "matched to a taxonomy family" and "other" buckets. */
export const splitLabelsByTaxonomy = (
  labels: ClassificationLabel[],
  taxonomy: ClassificationTaxonomy | null | undefined
) => {
  const familyMatchedLabels: ClassificationLabel[] = [];
  const otherLabels: ClassificationLabel[] = [];
  for (const label of labels) {
    if (resolveLabelToFamily(label.final_label, taxonomy)) {
      familyMatchedLabels.push(label);
    } else {
      otherLabels.push(label);
    }
  }
  return { familyMatchedLabels, otherLabels };
};
